/* eslint-disable max-statements */
import {useEffect, useState, useCallback} from 'react';
import {debounce} from 'lodash-es';

import sdk from '@/sdk';
import * as store from '@/store';
import eventBus, {EDITOR_DOM_READY, RECEIVE_NEW_GRADE} from '@/eventBus';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import {fetchDocToken} from '@/services';
import {EDITOR_MODE} from '@/Constant';
import {
    reportGradeGenerate,
    reportGradeDocEdit,
    reportGradeSuggest,
} from '@/buried/aigc';

import {NotifyAction, GradeStatus} from './interface';
import {getDefaultAIAgentData} from './constants';
import {getIsAuto} from './utils';

export const useAIAgentIsOpen = (mode: EDITOR_MODE, editable: boolean) => {
    const openAIAgent = !!sdk.user?.settings.newSettings.intelligent.openAIAgent
        && [EDITOR_MODE.edit, EDITOR_MODE.meeting].includes(mode);
    const isOpen = openAIAgent && editable; // 智能评分小流量下线

    return isOpen;
};

export const useAIAgent = (docGuid: string, mode: EDITOR_MODE, editable: boolean) => {
    const isOpen = useAIAgentIsOpen(mode, editable);

    useEffect(() => {
        const {AIGCInstanceRef, AIGCPluginReady} = store.getAIAgentData();
        // 因为 AIGCPluginReady 的时序问题，这里不重置相关数据
        store.setAIAgentData({
            ...getDefaultAIAgentData(),
            AIGCInstanceRef,
            AIGCPluginReady,
        });
    }, [docGuid, isOpen]);

    // 启动
    const startAgent = useCallback(() => {
        // 屏蔽演示模式
        if (store.getData(store.KEYS.DEMO_MODE)) {
            return;
        }
        const isAuto = getIsAuto();
        const {AIGCInstanceRef, docDomReady, AIGCPluginReady} = store.getAIAgentData();
        if (isOpen && docDomReady && AIGCPluginReady) {
            AIGCInstanceRef.current?.turnOnAIGCRatingAgent(isAuto);
        }
    }, [isOpen]);

    // 销毁
    useEffect(() => {
        return () => {
            const {AIGCInstanceRef} = store.getAIAgentData();
            AIGCInstanceRef.current?.destroy();
            AIGCInstanceRef.current = null;
        };
    }, [docGuid, isOpen]);

    useEffect(() => {
        // 加个防抖，避免EDITOR_DOM_READY事件触发两次的问题
        const onDomReady = debounce(() => {
            store.setAIAgentData({
                ...store.getAIAgentData(),
                docDomReady: true,
            });
            startAgent();

            if ([EDITOR_MODE.edit, EDITOR_MODE.meeting].includes(mode)) {
                const {current: editor} = store.getEditorRef();
                const wordCount = editor?.getDocLength({type: 'word'}) || 0;
                reportGradeDocEdit({docId: docGuid, wordCount});
                // isInGary().then((result) => {
                //     if (result) {
                //         const {current: editor} = store.getEditorRef();
                //         const wordCount = editor?.getDocLength({type: 'word'}) || 0;
                //         reportGradeDocEdit({docId: docGuid, wordCount});
                //     }
                // });
            }
        }, 1000);
        eventBus.on(EDITOR_DOM_READY, onDomReady);

        return () => {
            eventBus.off(EDITOR_DOM_READY, onDomReady);
        };
    }, [docGuid, mode, startAgent]);

    // 监听处理AIAgent通知
    const handleNotify = useCallback((payload: any) => {
        // 未开启时不做处理
        if (!isOpen) {
            return;
        }
        // 如果是演示模式，则不处理
        if (store.getData(store.KEYS.DEMO_MODE)) {
            return;
        }
        const {data, action} = payload;
        const AIAgentData = store.getAIAgentData();
        const {AIGCInstanceRef} = AIAgentData;

        if (action === NotifyAction.Ready) {
            // 替换新的实例前，销毁旧实例
            AIGCInstanceRef.current?.destroy();
            AIGCInstanceRef.current = data.object;
            store.setAIAgentData({
                ...AIAgentData,
                AIGCPluginReady: true,
            });

            // 提供更新token方法
            AIGCInstanceRef.current?.setUpdateTokenMethod(async () => {
                const curRepo = store.getCurRepository();
                const result = await fetchDocToken(curRepo!, docGuid);

                if (result) {
                    return result.accessToken;
                }
            });
        } else if (action === NotifyAction.ConditionChange) {
            let status = AIAgentData.gradeStatus;
            // 如果是不自动触发 status要以conditionChange为准
            const isAuto = getIsAuto();
            if (!isAuto || [GradeStatus.Init, GradeStatus.NoGradable].includes(AIAgentData.gradeStatus)) {
                status = data.status ? GradeStatus.Gradable : GradeStatus.NoGradable;
            }
            store.setAIAgentData({
                ...AIAgentData,
                wordCount: data.count,
                gradeStatus: status,
            });
        } else if (action === NotifyAction.UpdateAIResult) {
            store.setAIAgentData({
                ...AIAgentData,
                optimizeList: data.data || [],
                updateType: data.type,
                noTips: !!data.noTips,
                gradeStatus: data.type === 'request' ? GradeStatus.Success : AIAgentData.gradeStatus,
                wordCount: AIGCInstanceRef.current?.getEditorWordCount() || 0,
            });
            if (data.type === 'request') {
                reportGradeGenerate({docId: docGuid});

                if (data.data && Array.isArray(data.data) && data.data.length) {
                    const groupedData = data.data.reduce((obj: any, item: any) => {
                        if (!item.type) {
                            return obj;
                        }
                        const type = item.type === 'formatOptimize' ? `${item.type}-${item.formatType}` : item.type;
                        if (!obj[type]) {
                            obj[type] = [];
                        }
                        obj[type].push(item);
                        return obj;
                    }, {});
                    Object.entries(groupedData).forEach(([key, item]: any) => {
                        reportGradeSuggest({docId: docGuid, type: key, count: item.length});
                    });
                }
            }

        } else if (action === NotifyAction.ScoreChange) {
            store.setAIAgentData({
                ...AIAgentData,
                score: data.score,
                scoreUpdateType: data.type,
                // 手动评分完成后，置为false
                manualGrade: data.type === 'request' ? false : AIAgentData.manualGrade,
            });

            if (data.type === 'request') {
                eventBus.emit(RECEIVE_NEW_GRADE, data.score);
            }
        } else if (action === NotifyAction.HasNewRating) {
            store.setAIAgentData({
                ...AIAgentData,
                hasNewGrade: true,
            });
        } else if (action === NotifyAction.RatingLoading) {
            const val = data.status;
            let gradeStatus = GradeStatus.Gradable;
            const isAuto = getIsAuto();
            // 实在是 这逻辑实在是太难改了 火大 不敢动
            if (isAuto) {
                gradeStatus = val && AIAgentData.gradeStatus !== GradeStatus.Success
                    ? GradeStatus.Gradable
                    : AIAgentData.gradeStatus;
            } else {
                // ConditionChange 会在RatingLoading之前调用 所以如果gradeStatus是不可那就是不可
                gradeStatus = AIAgentData.gradeStatus === GradeStatus.NoGradable ? GradeStatus.NoGradable : (
                    val ? GradeStatus.Success : AIAgentData.gradeStatus
                );
            }
            store.setAIAgentData({
                ...AIAgentData,
                grading: val,
                gradeStatus,
            });
        } else if (action === NotifyAction.RatingFailed) {
            store.setAIAgentData({
                ...AIAgentData,
                gradeStatus: GradeStatus.Failed,
                grading: false,
            });
            // 当不自动评分情况下 会有无历史数据的情况 该情况展示无历史数据面板
        } else if (action === NotifyAction.RatingEmpty) {
            const isAuto = getIsAuto();
            !isAuto && store.setAIAgentData({
                ...AIAgentData,
                gradeStatus: GradeStatus.NoHistory,
                grading: false,
            });
        } else if (action === NotifyAction.SocketError) {
            store.setAIAgentData({
                ...AIAgentData,
                socketError: true,
            });
        }
    }, [docGuid, isOpen]);

    return handleNotify;
};
